/**
 * ========================================
 * API Service - CRM Web
 * ========================================
 * Cliente HTTP para endpoints del backend
 * Base URL: /api/crm-web
 *
 * Características:
 * - Auto-retry con exponential backoff
 * - JWT Bearer token automático
 * - Auto-logout en 401
 * - Verificación proactiva de expiración de token
 * - Auto-logout por inactividad (4 horas)
 * - Error handling centralizado
 */

import axios from 'axios'
import axiosRetry from 'axios-retry'

// ========================================
// CONFIGURACIÓN DE SESIÓN
// ========================================
const SESSION_CONFIG = {
  TOKEN_KEY: 'crm_token',
  USER_KEY: 'crm_user',
  LOGIN_TIMESTAMP_KEY: 'crm_login_at',
  LAST_ACTIVITY_KEY: 'crm_last_activity',
  TOKEN_EXPIRY_DAYS: 7,           // Token expira en 7 días
  PROACTIVE_CHECK_HOURS: 24,      // Verificar 24 horas antes de expirar
  INACTIVITY_TIMEOUT_HOURS: 4,    // Auto-logout después de 4 horas sin actividad
}

// ========================================
// UTILIDADES DE SESIÓN
// ========================================

/**
 * Decodifica el payload de un JWT (sin verificar firma)
 */
const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (e) {
    return null
  }
}

/**
 * Verifica inactividad del usuario
 * @returns {boolean} true si el usuario ha estado inactivo más del tiempo permitido
 */
const checkInactivity = () => {
  const lastActivity = localStorage.getItem(SESSION_CONFIG.LAST_ACTIVITY_KEY)
  if (!lastActivity) return false

  const lastActivityTime = parseInt(lastActivity, 10)
  const now = Date.now()
  const hoursInactive = (now - lastActivityTime) / (1000 * 60 * 60)

  return hoursInactive >= SESSION_CONFIG.INACTIVITY_TIMEOUT_HOURS
}

/**
 * Actualiza el timestamp de última actividad
 */
const updateLastActivity = () => {
  localStorage.setItem(SESSION_CONFIG.LAST_ACTIVITY_KEY, Date.now().toString())
}

/**
 * Flag para evitar múltiples llamadas a handleSessionExpired
 */
let sessionExpiredHandled = false

/**
 * Callback para notificar al store que la sesión expiró
 * Se configura desde el componente App
 */
let onSessionExpiredCallback = null

/**
 * Configura el callback de sesión expirada (llamado desde App.jsx)
 */
export const setSessionExpiredHandler = (callback) => {
  onSessionExpiredCallback = callback
}

/**
 * Resetea el flag de sesión expirada (llamado al hacer login exitoso)
 */
export const resetSessionExpiredFlag = () => {
  sessionExpiredHandled = false
}

/**
 * Maneja la expiración de sesión de forma limpia
 * @param {string} reason - Razón del logout para logging
 */
const handleSessionExpired = (reason) => {
  // Evitar múltiples llamadas simultáneas
  if (sessionExpiredHandled) return
  sessionExpiredHandled = true

  // [DIAG temporal 2026-06-30] Telemetria de logout -> backend (Railway logs), sin DevTools. Quitar tras diagnosticar a Ivan.
  try {
    const _tk = localStorage.getItem(SESSION_CONFIG.TOKEN_KEY)
    const _pl = _tk ? decodeJWT(_tk) : null
    let _user = null
    try { const _u = localStorage.getItem(SESSION_CONFIG.USER_KEY); _user = (_u && JSON.parse(_u)?.name) || _u } catch (e) { _user = null }
    const _diag = { type: 'logout', reason, user: _user, hasToken: !!_tk, tokenExp: _pl?.exp ?? null, tokenIat: _pl?.iat ?? null, nowSec: Math.floor(Date.now() / 1000), lastActivity: localStorage.getItem(SESSION_CONFIG.LAST_ACTIVITY_KEY), ua: navigator.userAgent, url: location.href }
    fetch('/api/crm-web/client-event', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(_diag), keepalive: true }).catch(() => {})
  } catch (e) { /* best-effort */ }

  console.warn(`🔒 Sesión expirada: ${reason}`)

  // [F2b] Invalidar tambien la cookie httpOnly server-side (best-effort). Sin esto el
  // auto-logout por inactividad seria solo cosmetico (la cookie seguiria viva 7d).
  try { fetch('/api/crm-web/auth/logout', { method: 'POST', keepalive: true }).catch(() => {}) } catch (_) {}

  // Limpiar TODOS los storages de sesión
  localStorage.removeItem(SESSION_CONFIG.TOKEN_KEY)
  localStorage.removeItem(SESSION_CONFIG.USER_KEY)
  localStorage.removeItem(SESSION_CONFIG.LOGIN_TIMESTAMP_KEY)
  localStorage.removeItem(SESSION_CONFIG.LAST_ACTIVITY_KEY)
  // CRÍTICO: También limpiar Zustand storage para evitar hidratación con datos viejos
  localStorage.removeItem('crm-web-storage')

  // Notificar al store para mostrar modal (sin redirect)
  if (onSessionExpiredCallback) {
    onSessionExpiredCallback()
  }
}

/**
 * Fuerza logout completo con redirect (para uso manual desde UI)
 * IMPORTANTE: Limpia TODOS los storages de sesión incluyendo Zustand
 */
export const forceLogoutWithRedirect = () => {
  // [F2] limpiar la cookie httpOnly server-side (best-effort)
  try { fetch('/api/crm-web/auth/logout', { method: 'POST', keepalive: true }).catch(() => {}) } catch (_) {}
  // Limpiar localStorage directo
  localStorage.removeItem(SESSION_CONFIG.TOKEN_KEY)
  localStorage.removeItem(SESSION_CONFIG.USER_KEY)
  localStorage.removeItem(SESSION_CONFIG.LOGIN_TIMESTAMP_KEY)
  localStorage.removeItem(SESSION_CONFIG.LAST_ACTIVITY_KEY)

  // CRÍTICO: Limpiar también el Zustand storage persistido
  // Sin esto, el store se hidrata con el token antiguo y causa loop de login
  localStorage.removeItem('crm-web-storage')

  sessionExpiredHandled = false
  window.location.href = '/crm-web/login'
}

// [2026-07-02] validateSession y checkTokenExpiry ELIMINADOS (post-F2 sin callers):
// la validez de la sesion la decide el server via cookie httpOnly + 401.

// [Hardening 2026-07-02, leccion INC-063] Un 401 aislado NO mata la sesion de inmediato:
// primero se verifica la cookie contra /auth/refresh (fetch crudo: no pasa por interceptors).
// - refresh 200     -> sesion VIVA: fue un endpoint con auth rota/propia; se registra en
//   telemetria (con la URL culpable) y NO se cierra nada. Un router olvidado ya no bota a todos.
// - refresh 401/403 -> cookie invalida de verdad -> flujo normal de sesion expirada (modal).
// - red caida / 5xx -> NO se mata la sesion (deploys/hiccups del server no deslogean).
let _probing401 = false
let _lastAliveProbeAt = 0
const handle401 = (sourceUrl) => {
  if (sessionExpiredHandled) return
  if (Date.now() - _lastAliveProbeAt < 10000) return // burst de polling: 1 probe cada 10s basta
  if (_probing401) return
  _probing401 = true
  fetch('/api/crm-web/auth/refresh', { method: 'POST' })
    .then((res) => {
      if (res.ok) {
        _lastAliveProbeAt = Date.now()
        console.warn(`[auth] 401 aislado con sesion VIVA (url: ${sourceUrl}) - no se cierra la sesion`)
        // Telemetria: la URL culpable es el dato clave para cazar el proximo router sin cookie
        try {
          let _user = null
          try { _user = JSON.parse(localStorage.getItem(SESSION_CONFIG.USER_KEY))?.name || null } catch (_) { _user = null }
          fetch('/api/crm-web/client-event', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
            body: JSON.stringify({ type: 'logout', reason: `401 aislado con sesion viva: ${sourceUrl || '?'}`, user: _user, hasToken: false, tokenExp: null, tokenIat: null, nowSec: Math.floor(Date.now() / 1000), lastActivity: localStorage.getItem(SESSION_CONFIG.LAST_ACTIVITY_KEY), ua: navigator.userAgent, url: location.href })
          }).catch(() => {})
        } catch (_) { /* best-effort */ }
      } else if (res.status === 401 || res.status === 403) {
        handleSessionExpired('Respuesta 401 del servidor (cookie verificada invalida)')
      } else {
        _lastAliveProbeAt = Date.now() // 5xx: server enfermo, no matar la sesion
      }
    })
    .catch(() => { /* sin red: no matar la sesion */ })
    .finally(() => { _probing401 = false })
}

// [2026-07-01] Refresco PROACTIVO por BODY. El mecanismo por header (X-Refreshed-Token) no llega
// en algunos navegadores (Brave) / proxies; el body siempre es legible. Si el token tiene >1 dia y
// sigue valido (<7d), pide uno fresco a /auth/refresh y lo guarda. Throttle 10 min. Corre en timer
// mientras la app este abierta + al validar sesion. (Historico pre-F2.)
// [F2 2026-07-02] Puente de transicion: si quedo un token legacy en localStorage
// (sesion pre-cookie), se canjea UNA vez por la cookie httpOnly via /auth/refresh y se
// elimina. Asi nadie re-loguea por la migracion. Si ya vencio, se limpia y el primer
// 401 lleva al login como siempre.
let _legacyBootstrapStarted = false
export const bootstrapLegacyCookie = async () => {
  if (_legacyBootstrapStarted) return
  _legacyBootstrapStarted = true
  try {
    const legacy = localStorage.getItem(SESSION_CONFIG.TOKEN_KEY)
    if (!legacy) return
    const res = await fetch('/api/crm-web/auth/refresh', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + legacy }
    })
    if (res.ok || res.status === 401) {
      localStorage.removeItem(SESSION_CONFIG.TOKEN_KEY)
      if (res.ok) console.log('sesion migrada a cookie httpOnly (token legacy canjeado)')
    }
  } catch (_) { /* sin red: se reintenta en el proximo boot */ }
}

// ========================================
// CREAR INSTANCIA DE AXIOS
// ========================================

const api = axios.create({
  baseURL: '/api/crm-web',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Configurar auto-retry (NO reintentar en 401)
axiosRetry(api, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    // NO reintentar en errores de autenticación
    if (error.response?.status === 401) return false
    return axiosRetry.isNetworkOrIdempotentRequestError(error)
      || error.response?.status === 429 // Rate limit
  }
})

// ========================================
// INTERCEPTORS
// ========================================

// Request: Agregar JWT token y verificar sesión
api.interceptors.request.use(
  (config) => {
    // [F2b] /auth/login NUNCA se bloquea: debe poder ejecutarse aunque el flag de sesion
    // expirada haya quedado prendido (si no, el re-login fallaria hasta recargar).
    if (config.url?.includes('/auth/login')) return config

    // Si la sesión ya expiró, cancelar request
    if (sessionExpiredHandled) {
      return Promise.reject(new Error('Sesión expirada'))
    }

    // Verificar sesión antes de cada request
    // [F2b] Actividad SIEMPRE: el guard if(token) quedo muerto (ya no hay token en JS)
    {
      // Verificar si el token ya expiró antes de enviar
      // [F2 2026-07-02] La sesion viaja en la cookie httpOnly (same-origin, el browser
      // la adjunta solo). Sin token en JS => sin chequeos client-side de expiracion
      // (eran la fuente de los loops con storage no-durable). El server decide via 401.

      // Actualizar última actividad en cada request
      updateLastActivity()
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response: Manejar errores globales
// [F2] El sliding de sesion lo maneja el server re-seteando la cookie httpOnly en cada
// respuesta elegible; el frontend no captura ni guarda tokens.

api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // 401 → verificar la cookie antes de declarar la sesion expirada (handle401)
    // PERO NO para el endpoint de login (ese 401 significa credenciales incorrectas)
    const isLoginEndpoint = error.config?.url?.includes('/auth/login')

    if (error.response?.status === 401 && !isLoginEndpoint) {
      handle401((error.config?.baseURL || '') + (error.config?.url || ''))
    }

    return Promise.reject(error)
  }
)

// ========================================
// AUTH
// ========================================

export const authAPI = {
  /**
   * Login con teléfono y contraseña
   * @param {string} phone - Teléfono del agente
   * @param {string} password - Contraseña
   * @returns {Promise<{token: string, user: object}>}
   */
  login: async (phone, password) => {
    const response = await api.post('/auth/login', { phone, password })

    // Resetear flag de sesion expirada
    resetSessionExpiredFlag()

    // Guardar en localStorage (usar constantes de SESSION_CONFIG)
    // [F2] El token ya NO se guarda en JS: la sesion quedo en la cookie httpOnly
    // (Set-Cookie del login). Limpieza de token legacy si quedaba.
    localStorage.removeItem(SESSION_CONFIG.TOKEN_KEY)
    localStorage.setItem(SESSION_CONFIG.USER_KEY, JSON.stringify(response.user))
    localStorage.setItem(SESSION_CONFIG.LOGIN_TIMESTAMP_KEY, Date.now().toString())
    localStorage.setItem(SESSION_CONFIG.LAST_ACTIVITY_KEY, Date.now().toString())

    return response
  },

  /**
   * Logout (limpiar localStorage y Zustand storage)
   */
  logout: () => {
    // [F2] limpiar la cookie httpOnly server-side (best-effort, sobrevive navegacion)
    try { fetch('/api/crm-web/auth/logout', { method: 'POST', keepalive: true }).catch(() => {}) } catch (_) {}
    localStorage.removeItem(SESSION_CONFIG.TOKEN_KEY)
    localStorage.removeItem(SESSION_CONFIG.USER_KEY)
    localStorage.removeItem(SESSION_CONFIG.LOGIN_TIMESTAMP_KEY)
    localStorage.removeItem(SESSION_CONFIG.LAST_ACTIVITY_KEY)
    // También limpiar Zustand storage para evitar hidratación con datos viejos
    localStorage.removeItem('crm-web-storage')
  },

  /**
   * Obtener usuario actual de localStorage
   * @returns {object|null}
   */
  getCurrentUser: () => {
    const userJson = localStorage.getItem(SESSION_CONFIG.USER_KEY)
    return userJson ? JSON.parse(userJson) : null
  },

  /**
   * Verificar si hay sesión activa (incluye validación de expiración)
   * @returns {boolean}
   */
  isAuthenticated: () => {
    // [F2] La validez REAL la decide el server (cookie httpOnly + 401 en respuesta).
    // Client-side solo: hay usuario recordado y no lleva 4h inactivo.
    try {
      if (!authAPI.getCurrentUser()) return false
    } catch (_) { return false }
    if (checkInactivity()) {
      handleSessionExpired(`Inactividad de m\u00e1s de ${SESSION_CONFIG.INACTIVITY_TIMEOUT_HOURS} horas`)
      return false
    }
    updateLastActivity()
    return true
  }
}

// ========================================
// CHATS
// ========================================

export const chatsAPI = {
  /**
   * Obtener lista de chats con paginación y búsqueda
   * @param {number} limit - Cantidad de chats a obtener (default: 50)
   * @param {number} offset - Offset para paginación (default: 0)
   * @param {string} search - Término de búsqueda (opcional)
   * @returns {Promise<{chats: Array, pagination: {total, limit, offset, hasMore}}>}
   */
  getChats: async (limit = 50, offset = 0, search = '') => {
    const params = new URLSearchParams()
    params.append('limit', limit)
    params.append('offset', offset)
    if (search) params.append('search', search)
    return await api.get(`/chats?${params.toString()}`)
  },

  /**
   * Obtener actualizaciones ligeras (solo timestamps y contadores, sin avatares)
   * @returns {Promise<{updates: Array<{phone, lastMessage, lastMessageTime, unreadCount}>}>}
   */
  getChatsUpdates: async () => {
    return await api.get('/chats/updates')
  },

  /**
   * Marcar chat como leido
   * @param {string} phone - Telefono del contacto
   */
  markAsRead: async (phone) => {
    return await api.post(`/chats/${phone}/mark-read`)
  }
}

// ========================================
// MESSAGES
// ========================================

export const messagesAPI = {
  /**
   * Obtener historial de mensajes
   * @param {string} phone - Teléfono del contacto
   * @param {number} limit - Cantidad de mensajes (default 100)
   * @returns {Promise<Array>}
   */
  getMessages: async (phone, limit = 100) => {
    return await api.get(`/messages/${phone}?limit=${limit}`)
  },

  /**
   * Ultimos N mensajes del cliente (popup hover grid)
   */
  getLastClientMessages: async (phone, limit = 3) => {
    return await api.get(`/messages/last-client/${phone}?limit=${limit}`)
  },

  /**
   * Enviar mensaje
   * @param {string} phone - Teléfono del destinatario
   * @param {string} text - Texto del mensaje
   * @returns {Promise<object>}
   */
  sendMessage: async (phone, text) => {
    return await api.post('/messages/send', { phone, text })
  },

  /**
   * Obtener URL de media por fileId
   * @param {string} fileId - ID del archivo en Wassenger
   * @returns {Promise<{url, mimetype, name, size, preview}>}
   */
  getMediaUrl: async (fileId) => {
    try {
      return await api.get(`/messages/media/${fileId}`)
    } catch (error) {
      console.error('Error obteniendo media URL:', error)
      return { error: true }
    }
  },
}

// ========================================
// PROSPECT
// ========================================

export const prospectAPI = {
  /**
   * Obtener datos del prospecto
   * @param {string} phone - Teléfono del prospecto
   * @returns {Promise<object>}
   */
  getProspect: async (phone) => {
    return await api.get(`/prospect/${phone}`)
  },

  // Asignar/limpiar avatar de rostro (avatar_seed). seed=null vuelve a iniciales.
  setAvatar: (phone, seed) =>
    api.put(`/prospect/${encodeURIComponent(phone)}/avatar`, { avatar_seed: seed }),

  /**
   * Actualizar datos del prospecto
   * @param {string} phone - Teléfono del prospecto
   * @param {object} updates - Campos a actualizar
   * @returns {Promise<object>}
   */
  syncAssignment: (phone) =>
    api.get(`/prospect/${encodeURIComponent(phone)}/sync-assignment`).then(r => r.data),

  updateProspect: async (phone, updates) => {
    return await api.put(`/prospect/${phone}`, updates)
  }
}

// ========================================
// CONTACT
// ========================================

export const contactAPI = {
  /**
   * Obtener avatar del contacto desde Whapi
   * @param {string} phone - Teléfono del contacto
   * @returns {Promise<{avatar: string|null, name: string|null, pushname: string|null}>}
   */
  getAvatar: async (phone) => {
    try {
      return await api.get(`/contact/${phone}/avatar`)
    } catch (error) {
      // Si falla, retornar null para usar avatar por defecto
      return { avatar: null, name: null, pushname: null }
    }
  },

  /**
   * Obtener avatares de múltiples contactos en batch
   * @param {Array<string>} phones - Array de teléfonos
   * @returns {Promise<Object>} - Objeto con teléfonos como keys y {avatar, cached} como values
   */
  getAvatarsBatch: async (phones) => {
    try {
      return await api.post('/contacts/avatars/batch', { phones })
    } catch (error) {
      console.error('Error obteniendo avatares batch:', error)
      return {}
    }
  },

  /**
   * Verificar si un teléfono ya existe en la base de datos
   * @param {string} phone - Teléfono a verificar
   * @returns {Promise<{exists: boolean, name?: string, phone?: string}>}
   */
  checkDuplicate: async (phone) => {
    return await api.get(`/contacts/check-duplicate/${encodeURIComponent(phone)}`)
  },

  /**
   * Crear un contacto manualmente
   * @param {Object} data - { phone, name?, agentId?, dueDate?, folderId?, newListName? }
   * @returns {Promise<{id: number, phone: string, name: string}>}
   */
  createContact: async (data) => {
    return await api.post('/contacts/create', data)
  }
}

// ========================================
// ICONS
// ========================================

export const iconsAPI = {
  /**
   * Obtener todos los iconos disponibles
   * @returns {Promise<Array>}
   */
  getAvailableIcons: async () => {
    return await api.get('/prospect-icons')
  },

  /**
   * Obtener iconos asignados a un prospecto
   * @param {string} phone - Teléfono del prospecto
   * @returns {Promise<Array>}
   */
  getProspectIcons: async (phone) => {
    return await api.get(`/prospect/${phone}/icons`)
  },

  /**
   * Actualizar iconos de un prospecto
   * @param {string} phone - Teléfono del prospecto
   * @param {Array<number>} iconIds - IDs de los iconos (máximo 3)
   * @returns {Promise<Array>}
   */
  updateProspectIcon: async (phone, iconId) => {
    return await api.put(`/prospect/${phone}/icons`, { iconId })
  },

  /**
   * Obtener conteo de prospectos por icono con filtros opcionales
   * @param {Object} filters - Filtros opcionales: work_area, folder_id, label, department, flag_filter
   * @returns {Promise<Array>}
   */
  getCounts: (filters) => {
    const params = new URLSearchParams();
    if (filters?.work_area) params.set('work_area', filters.work_area);
    if (filters?.folder_id) params.set('folder_id', filters.folder_id);
    if (filters?.label) params.set('label', filters.label);
    if (filters?.department) params.set('department', filters.department);
    if (filters?.flag_filter) params.set('flag_filter', filters.flag_filter);
    if (filters?.agent) params.set('agent', filters.agent);
    return api.get(`/icons/counts?${params.toString()}`);
  }
}

// ========================================
// LISTS (ToDoList View) - Ex-Folders/Carpetas
// ========================================

export const foldersAPI = {
  /**
   * Obtener todas las listas
   * @param {boolean} includeArchived - Incluir listas archivadas
   */
  getFolders: async (includeArchived = false) => {
    return await api.get(`/folders?include_archived=${includeArchived}`)
  },

  /**
   * Crear nueva lista
   */
  createFolder: async (nombre, parentId = null, color = '#6B7280', icono = '📁', descripcion = null) => {
    return await api.post('/folders', { nombre, parent_id: parentId, color, icono, descripcion })
  },

  /**
   * Actualizar lista (nombre, color, icono, descripcion, archivada)
   */
  updateFolder: async (id, data) => {
    return await api.put(`/folders/${id}`, data)
  },

  /**
   * Eliminar lista
   */
  deleteFolder: async (id) => {
    return await api.delete(`/folders/${id}`)
  },

  /**
   * Agregar contacto a lista
   */
  addContactToFolder: async (folderId, contactoId) => {
    return await api.post(`/folders/${folderId}/contacts`, { contacto_id: contactoId })
  },

  /**
   * Quitar contacto de lista
   */
  removeContactFromFolder: async (folderId, contactoId) => {
    return await api.delete(`/folders/${folderId}/contacts/${contactoId}`)
  },

  /**
   * Mover contacto entre listas (drag & drop)
   */
  moveContact: async (targetFolderId, contactoId, sourceFolderId = null) => {
    return await api.put(`/folders/${targetFolderId}/contacts/${contactoId}/move`, {
      sourceFolderId
    })
  },

  /**
   * Reordenar listas (drag & drop)
   */
  reorderFolders: async (orderedIds) => {
    return await api.put('/folders/reorder', { orderedIds })
  }
}

// ========================================
// TODOLIST (Contacts Table)
// ========================================

export const todolistAPI = {
  getWorkAreaCounts: async (agent) => api.get("/todolist/work-area-counts" + (agent ? "?agent=" + encodeURIComponent(agent) : "")),
  // Manitos en vivo: set global liviano de last_from_client=true (+flags urgente/fondo)
  getHands: async () => api.get("/todolist/hands"),
  /**
   * Obtener contactos para vista ToDoList con paginación
   * @param {Object} filters - { folder_id, agent, label, department, search, limit, offset }
   * @returns {Promise<{contacts: Array, total: number}>}
   */
  getContacts: async (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.folder_id) params.append('folder_id', filters.folder_id)
    if (filters.agent) params.append('agent', filters.agent)
    if (filters.label) params.append('label', filters.label)
    if (filters.department) params.append('department', filters.department)
    if (filters.search) params.append('search', filters.search)
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.offset) params.append('offset', filters.offset)
    if (filters.work_area) params.append('work_area', filters.work_area)
    if (filters.icon_filter) params.append('icon_filter', filters.icon_filter)
    if (filters.flag_filter) params.append('flag_filter', filters.flag_filter)
    if (filters.draft_filter) params.append('draft_filter', filters.draft_filter)
    if (filters.action_filter) params.append('action_filter', filters.action_filter)
    if (filters.action_agent) params.append('action_agent', filters.action_agent)
    if (filters.ic_quality) params.append('ic_quality', filters.ic_quality)
    if (filters.bulb_recent) params.append('bulb_recent', filters.bulb_recent)
    if (filters.wrote_today) params.append('wrote_today', filters.wrote_today)
    if (filters.sort_by) params.append('sort_by', filters.sort_by)
    if (filters.sort_dir) params.append('sort_dir', filters.sort_dir)
    if (filters.sort) params.append('sort', filters.sort)
    if (filters.sort_preset) params.append('sort_preset', filters.sort_preset)
    if (filters.next_followup_date) params.append('next_followup_date', filters.next_followup_date)
    if (filters.include_worked_today) params.append('include_worked_today', filters.include_worked_today)
    return await api.get(`/todolist/contacts?${params.toString()}`)
  },

  /**
   * Obtener contactos nuevos desde un timestamp (para auto-refresh)
   * @param {string} since - Timestamp ISO
   * @returns {Promise<{contacts: Array, count: number}>}
   */
  getNewContacts: async (since) => {
    return await api.get(`/todolist/contacts/new?since=${encodeURIComponent(since)}`)
  },

  /**
   * Obtener etiquetas de Wassenger con contadores
   */
  getLabels: async () => {
    return await api.get('/filters/labels')
  },

  /**
   * Obtener departamentos de Wassenger con contadores
   */
  getDepartments: async () => {
    return await api.get('/filters/departments')
  },

  /**
   * Actualizar fecha de vencimiento masivamente
   * @param {Array<number>} contactIds - IDs de contactos
   * @param {string|null} date - Fecha en formato YYYY-MM-DD o null para quitar
   */
  updateBulkDate: async (contactIds, date) => {
    return await api.put('/todolist/contacts/bulk-date', {
      contactIds,
      date
    })
  },

  /**
   * Agregar contactos a carpeta masivamente
   * @param {Array<number>} contactIds - IDs de contactos
   * @param {number} folderId - ID de la carpeta
   */
  bulkAddToFolder: async (contactIds, folderId) => {
    return await api.post('/todolist/contacts/bulk-folder', {
      contactIds,
      folderId
    })
  },

  /**
   * Obtener contactos con fecha de vencimiento próxima
   * @param {number} days - Días hacia adelante (default 7)
   */
  getUpcoming: async (days = 7) => {
    return await api.get(`/todolist/upcoming?days=${days}`)
  },

  /**
   * Obtener IDs de contactos que matchean los filtros (sin payload completo).
   * Usado por bulk-cohort-from-filter para construir cohortes desde grilla filtrada.
   * @param {Object} filters - { work_area, folder_id, label, department, flag_filter, icon_filter, agent, search, include_worked_today }
   * @returns {Promise<{success: boolean, ids: number[], total: number, capped: boolean}>}
   */
  getContactIds: async (filters = {}) => {
    const allowed = ['work_area', 'folder_id', 'label', 'department', 'flag_filter', 'icon_filter', 'draft_filter', 'action_filter', 'action_agent', 'ic_quality', 'bulb_recent', 'wrote_today', 'agent', 'search', 'include_worked_today']
    const params = {}
    for (const k of allowed) {
      if (filters[k] !== undefined && filters[k] !== null && filters[k] !== '') {
        params[k] = filters[k]
      }
    }
    return await api.get('/todolist/contacts/ids', { params })
  },
}

// ========================================
// NOTES
// ========================================

export const notesAPI = {
  /**
   * Obtener notas de un contacto
   */
  getNotes: async (phone) => {
    return await api.get(`/prospect/${phone}/notes`)
  },

  /**
   * Parsear nota y detectar acciones (sin guardar)
   */
  parseNote: async (phone, nota) => {
    return await api.post(`/prospect/${phone}/notes/parse`, { nota })
  },

  /**
   * Agregar nota con acciones opcionales
   * @param {string} phone - Teléfono del contacto
   * @param {string} nota - Texto de la nota
   * @param {Array} applyActions - Acciones a aplicar
   */
  addNote: async (phone, nota, applyActions = [], structured = null, options = {}) => {
    const body = { nota, applyActions }
    if (structured) {
      body.accion = structured.accion
      body.accion_siguiente = structured.accion_siguiente
      body.accion_siguiente_fecha = structured.accion_siguiente_fecha
    }
    if (options.markIcPending === true) {
      body.mark_ic_pending = true
    }
    return await api.post(`/prospect/${phone}/notes`, body)
  },

  /**
   * Actualizar nota
   */
  updateNote: async (phone, noteId, nota, applyActions = []) => {
    return await api.put(`/prospect/${phone}/notes/${noteId}`, { nota, applyActions })
  },

  /**
   * Eliminar nota
   */
  deleteNote: async (phone, noteId) => {
    return await api.delete(`/prospect/${phone}/notes/${noteId}`)
  }
}

// ========================================
// CALL TRACKING
// ========================================

export const callsAPI = {
  /**
   * Obtener llamadas activas del agente (para recovery al recargar)
   */
  getActiveCalls: async () => {
    return await api.get('/calls/active')
  },

  /**
   * Iniciar llamada con un contacto
   * @param {number} contactoId - ID del contacto en contactos_dm
   */
  startCall: async (contactoId) => {
    return await api.post('/calls/start', { contactoId })
  },

  /**
   * Terminar una llamada activa
   * @param {number} callId - ID de la llamada
   * @param {string} notes - Notas opcionales
   */
  endCall: async (callId, notes = null) => {
    return await api.put(`/calls/${callId}/end`, { notes })
  },

  /**
   * Obtener historial de llamadas de un contacto
   * @param {string} phone - Teléfono del contacto
   */
  getContactCalls: async (phone) => {
    return await api.get(`/prospect/${phone}/calls`)
  }
}

// ========================================
// CAMPAIGNS (CRM Drip Campaigns)
// ========================================

// Instancia separada para API de campañas (diferente base URL)
const campaignsApi = axios.create({
  baseURL: '/api/crm-campaigns',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
})

// Interceptors para campaignsApi
campaignsApi.interceptors.request.use((config) => {
  // [F2] Sesion por cookie httpOnly; el browser la adjunta solo (same-origin).
  updateLastActivity()
  return config
})

campaignsApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) handle401((error.config?.baseURL || '') + (error.config?.url || ''))
    return Promise.reject(error)
  }
)

export const campaignsAPI = {
  // --- Campañas ---
  getCampaigns: async () => campaignsApi.get('/'),
  getCampaign: async (id) => campaignsApi.get(`/${id}`),
  createCampaign: async (data) => campaignsApi.post('/', data),
  updateCampaign: async (id, data) => campaignsApi.put(`/${id}`, data),
  deleteCampaign: async (id) => campaignsApi.delete(`/${id}`),
  duplicateCampaign: async (id) => campaignsApi.post(`/${id}/duplicate`),
  getCampaignStats: async (id) => campaignsApi.get(`/${id}/stats`),
  // Modo instantáneo (admin): ON = inscripciones nuevas envían pasos de inmediato encadenados
  setInstantMode: async (id, enabled) => campaignsApi.patch(`/${id}/instant-mode`, { enabled }),
  // Trigger por keyword en la Nota Express (admin)
  setKeywordTrigger: async (id, { enabled, keyword, matchMode }) =>
    campaignsApi.patch(`/${id}/keyword-trigger`, { enabled, keyword, match_mode: matchMode }),

  // --- Steps ---
  getSteps: async (campaignId) => campaignsApi.get(`/${campaignId}/steps`),
  createStep: async (campaignId, data, options = {}) => {
    const { insertMode } = options
    const queryStr = insertMode === 'shift' ? '?insert_mode=shift' : ''
    return campaignsApi.post(`/${campaignId}/steps${queryStr}`, data)
  },
  updateStep: async (campaignId, stepId, data) => campaignsApi.put(`/${campaignId}/steps/${stepId}`, data),
  deleteStep: async (campaignId, stepId) => campaignsApi.delete(`/${campaignId}/steps/${stepId}`),

  // --- Enrollments ---
  getEnrollments: async (campaignId, status = null) => {
    const params = status ? `?status=${status}` : ''
    return campaignsApi.get(`/${campaignId}/enrollments${params}`)
  },
  enrollContact: async (campaignId, contactPhone) => campaignsApi.post(`/${campaignId}/enroll`, { contactPhone }),
  exitContact: async (campaignId, contactPhone, reason = 'manual') => campaignsApi.post(`/${campaignId}/exit`, { contactPhone, reason }),

  // --- Filtros ---
  getWassengerAgents: async () => campaignsApi.get('/filters/agents'),
  getWassengerDepartments: async () => campaignsApi.get('/filters/departments'),
  getWassengerLabels: async () => campaignsApi.get('/filters/labels'),
  getFolders: async () => campaignsApi.get('/filters/folders'),

  // --- Archivos Wassenger ---
  getFilePreview: async (fileId) => campaignsApi.get(`/files/${fileId}/preview`),

  // --- Bulk Operations ---
  getAvailableContacts: async (campaignId, filters = {}) => {
    const params = new URLSearchParams()
    if (filters.search) params.append('search', filters.search)
    if (filters.assignedTo) params.append('assignedTo', filters.assignedTo)
    if (filters.clasificacion) params.append('clasificacion', filters.clasificacion)
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom)
    if (filters.dateTo) params.append('dateTo', filters.dateTo)
    if (filters.createdFrom) params.append('createdFrom', filters.createdFrom)
    if (filters.createdTo) params.append('createdTo', filters.createdTo)
    if (filters.department) params.append('department', filters.department)
    if (filters.label) params.append('label', filters.label)
    if (filters.fromCampaign) params.append('fromCampaign', filters.fromCampaign)
    if (filters.folder) params.append('folder', filters.folder)
    if (filters.limit) params.append('limit', filters.limit)
    if (filters.offset) params.append('offset', filters.offset)
    return campaignsApi.get(`/${campaignId}/available-contacts?${params.toString()}`)
  },
  getAllAvailableContactIds: async (campaignId, filters = {}) => {
    const params = new URLSearchParams()
    if (filters.search) params.append('search', filters.search)
    if (filters.assignedTo) params.append('assignedTo', filters.assignedTo)
    if (filters.clasificacion) params.append('clasificacion', filters.clasificacion)
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom)
    if (filters.dateTo) params.append('dateTo', filters.dateTo)
    if (filters.createdFrom) params.append('createdFrom', filters.createdFrom)
    if (filters.createdTo) params.append('createdTo', filters.createdTo)
    if (filters.department) params.append('department', filters.department)
    if (filters.label) params.append('label', filters.label)
    if (filters.fromCampaign) params.append('fromCampaign', filters.fromCampaign)
    if (filters.folder) params.append('folder', filters.folder)
    return campaignsApi.get(`/${campaignId}/available-contacts-ids?${params.toString()}`)
  },
  enrollContactsBulk: async (campaignId, contactIds) => campaignsApi.post(`/${campaignId}/enroll-bulk`, { contactIds }),
  exitContactsBulk: async (campaignId, contactIds, reason = 'manual_admin') => campaignsApi.post(`/${campaignId}/exit-bulk`, { contactIds, reason }),

  // --- Clasificación ---
  getClasificacionStats: async () => campaignsApi.get('/clasificacion/stats'),
  getContactClasificacion: async (phone) => campaignsApi.get(`/clasificacion/${phone}`),
  setContactClasificacion: async (phone, clasificacion) => campaignsApi.post(`/clasificacion/${phone}`, { clasificacion }),

  // --- Rotación de agentes ---
  getRotationConfigs: async () => campaignsApi.get('/rotation/configs'),
  getRotationStats: async () => campaignsApi.get('/rotation/stats'),
  updateRotationConfig: async (type, data) => campaignsApi.put(`/rotation/${type}`, data),

  // --- Contacto ---
  getContactEnrollments: async (phone) => campaignsApi.get(`/contact/${phone}/enrollments`),
  exitContactFromAll: async (phone, reason = 'manual') => campaignsApi.post(`/contact/${phone}/exit-all`, { reason })
}

// ============================================
// WASSENGER API (Sprint 2)
// ============================================
export const wassengerAPI = {
  // Catálogos
  getLabels: async () => {
    const response = await api.get('/wassenger/labels')
    return response.data
  },
  getDepartments: async () => {
    const response = await api.get('/wassenger/departments')
    return response.data
  },

  // Asignaciones
  assignLabels: async (phone, labels) => {
    const response = await api.post(`/wassenger/chat/${phone}/labels`, { labels })
    return response.data
  },
  assignDepartment: async (phone, department) => {
    const response = await api.post(`/wassenger/chat/${phone}/department`, { department })
    return response.data
  },

  // Equipo y asignación de agentes
  getTeam: async () => {
    return await api.get('/wassenger/team')
  },
  assignAgent: async (phone, agentId) => {
    return await api.patch(`/wassenger/chat/${phone}/agent`, { agentId })
  },
  unassignAgent: async (phone) => {
    return await api.delete(`/wassenger/chat/${phone}/agent`)
  }
}

// ============================================
// SYNC ADMIN API (LibertadSync v3.0 - Monitoreo)
// ============================================

const syncAdminApi = axios.create({
  baseURL: '/api/crm-web/sync-admin',
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
})

syncAdminApi.interceptors.request.use((config) => {
  // [F2] Sesion por cookie httpOnly; el browser la adjunta solo (same-origin).
  updateLastActivity()
  return config
})

syncAdminApi.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) handle401((error.config?.baseURL || '') + (error.config?.url || ''))
    return Promise.reject(error)
  }
)

export const syncAdminAPI = {
  // Dashboard
  getSummary: async () => syncAdminApi.get('/dashboard/summary'),

  // Instances
  getInstances: async () => syncAdminApi.get('/instances'),
  getInstance: async (id) => syncAdminApi.get(`/instances/${id}`),
  getInstanceLogs: async (id, limit = 50) => syncAdminApi.get(`/instances/${id}/logs?limit=${limit}`),
  getInstanceErrors: async (id, limit = 50) => syncAdminApi.get(`/instances/${id}/errors?limit=${limit}`),
  getInstanceCommands: async (id, limit = 20) => syncAdminApi.get(`/instances/${id}/commands?limit=${limit}`),

  // Commands
  sendCommand: async (instanceId, command, params = {}) =>
    syncAdminApi.post(`/instances/${instanceId}/command`, { command, params }),
  cancelCommand: async (cmdId) => syncAdminApi.delete(`/commands/${cmdId}`),

  // Authorizations
  getAuthorizations: async () => syncAdminApi.get('/authorizations'),
  approve: async (installationId) => syncAdminApi.post(`/authorizations/${installationId}/approve`),
  reject: async (installationId, reason) =>
    syncAdminApi.post(`/authorizations/${installationId}/reject`, { reason }),
}

// ========================================
// SUPERVISOR IA
// ========================================
export const supervisorAPI = {
  getAnalysis: async (phone) => api.get(`/supervisor/analysis/${phone}`),
  triggerAnalysis: async (phone) => api.post(`/supervisor/analyze/${phone}`, {}, { timeout: 30000 }),
}


// ========================================
// NOTIFICATIONS
// ========================================
export const notificationAPI = {
  getPending: async () => api.get('/notifications/pending'),
  markDelivered: async (id) => api.post(`/notifications/${id}/deliver`),
  performAction: async (id, action, minutes) => api.post(`/notifications/${id}/action`, minutes != null ? { action, minutes } : { action }),
  getConfig: async () => api.get('/notifications/config'),
  updateConfig: async (config) => api.put('/notifications/config', config),
}


// ========================================
// NOTE DRAFTS
// ========================================
export const draftsAPI = {
  saveDraft: async (contacto_phone, draft_text) =>
    api.post('/notes/draft', { contacto_phone, draft_text }),
  getDraft: async (phone) =>
    api.get(`/notes/draft/${phone}`),
  listDraftPhones: async () =>
    api.get('/notes/drafts'),
  deleteDraft: async (phone) =>
    api.delete(`/notes/draft/${phone}`),
}

// ========================================
// INCOMING NOTIFICATIONS
// ========================================
export const incomingNotifAPI = {
  getNotifications: async () => api.get('/incoming-notifications'),
}

// ========================================
// DAILY STATS
// ========================================
export const statsAPI = {
  getDaily: async (agentName = null) => {
    const params = agentName ? `?agent=${encodeURIComponent(agentName)}` : '';
    return api.get(`/stats/daily${params}`);
  },
  // [2026-07-02] increment ELIMINADO: escribia a agent_daily_stats (tabla muerta, DROPeada).
}

// ========================================
// BULK ACTIONS
// ========================================
export const bulkAPI = {
  updateIcon: async (phones, icon_id) =>
    api.put('/todolist/bulk-icon', { phones, icon_id }),
  assign: async (phones, agent_id, agent_name) =>
    api.put('/todolist/bulk-assign', { phones, agent_id, agent_name }),
  bulkFlag: async (phones, bandera) => {
    return await api.put('/todolist/bulk-flag', { phones, bandera })
  },
  smartReschedulePreview: async (contactIds, opts = {}) => api.post('/todolist/smart-reschedule/preview', { contactIds, ...opts }),
  smartRescheduleApply: async (assignments) => api.post('/todolist/smart-reschedule/apply', { assignments }),
  addToCampaign: async ({ campaignId, startDate, contactIds }) => {
    const cohort = await cohortsAPI.create({ campaignId, startDate })
    return await cohortsAPI.addEnrollments(cohort.cohort.id, contactIds)
  },
}

// ========================================
// ANALYTICS
// ========================================
export const adminAPI = {
  getRescheduleConfig: async () => api.get('/admin/reschedule-config'),
  saveRescheduleConfig: async (rows) => api.put('/admin/reschedule-config', rows),
  getCampaignCategories: async () => api.get('/campaign-categories'),
  createCampaignCategory: async (data) => api.post('/campaign-categories', data),
  updateCampaignCategory: async (id, data) => api.put(`/campaign-categories/${id}`, data),
}

export const analyticsAPI = {
  getOverview: async (range = '7d') =>
    api.get(`/analytics/overview?range=${encodeURIComponent(range)}`),

  getAgents: async (range = '7d', excludeAdmin = true) => {
    const q = new URLSearchParams({ range })
    q.set('exclude_admin', excludeAdmin ? '1' : '0')
    return api.get(`/analytics/agents?${q.toString()}`)
  },

  getClassification: async (range = '7d', agentId = null, excludeAdmin = true) => {
    const q = new URLSearchParams({ range })
    if (agentId) q.set('agent_id', agentId)
    q.set('exclude_admin', excludeAdmin ? '1' : '0')
    return api.get(`/analytics/classification?${q.toString()}`)
  },

  getVolume: async (range = '7d') =>
    api.get(`/analytics/volume?range=${encodeURIComponent(range)}`),

  scheduledHeatmap: (days = 30) =>
    api.get(`/analytics/scheduled-heatmap?days=${days}`),
  scheduledCompliance: (days = 30) =>
    api.get(`/analytics/scheduled-compliance?days=${days}`),
  scheduledComplianceCell: (agent, date) =>
    api.get(`/analytics/scheduled-compliance/cell?agent=${encodeURIComponent(agent)}&date=${date}`),
  contactability: (days = 30) =>
    api.get(`/analytics/contactability?days=${days}`),
  contactabilityCell: (agent, date) =>
    api.get(`/analytics/contactability/cell?agent=${encodeURIComponent(agent)}&date=${date}`),
}

// ========================================
// AUDIT / CHANGE HISTORY
// ========================================
export const auditAPI = {
  getChanges: ({ range = '24h', order = 'desc', agent, include_bot = false, limit = 200, start_at, end_at } = {}) => {
    const params = new URLSearchParams({
      range,
      order,
      include_bot: String(include_bot),
      limit: String(limit),
    })
    if (agent) params.set('agent', agent)
    if (range === 'custom' && start_at) params.set('start_at', start_at)
    if (range === 'custom' && end_at) params.set('end_at', end_at)
    return api.get(`/audit/changes?${params.toString()}`)
  }
}

// ==========================================
// COHORTS API
// ==========================================
export const cohortsAPI = {
  async create({ campaignId, startDate }) {
    const r = await campaignsApi.post(`/${campaignId}/cohorts`, { start_date: startDate })
    return r
  },
  async list(campaignId) {
    const r = await campaignsApi.get(`/${campaignId}/cohorts`)
    return r
  },
  async get(cohortId) {
    const r = await campaignsApi.get(`/cohorts/${cohortId}`)
    return r
  },
  async getFailures(cohortId) {
    const r = await campaignsApi.get(`/cohorts/${cohortId}/failures`)
    return r
  },
  async preview({ campaignId, startDate, contactIds }) {
    const r = await campaignsApi.post('/cohorts/preview', { campaign_id: campaignId, start_date: startDate, contact_ids: contactIds })
    return r
  },
  async addEnrollments(cohortId, contactIds) {
    const r = await campaignsApi.post(`/cohorts/${cohortId}/enrollments`, { contact_ids: contactIds })
    return r
  },
  async cancel(cohortId, reason) {
    const r = await campaignsApi.post(`/cohorts/${cohortId}/cancel`, { reason })
    return r
  },
  async editStartDate(cohortId, newStartDate) {
    const r = await campaignsApi.put(`/cohorts/${cohortId}/start-date`, { start_date: newStartDate })
    return r
  },
  async getCalendar({ from, to, onlyMine = false }) {
    const r = await campaignsApi.get('/calendar/cohorts', {
      params: { from, to, onlyMine: onlyMine ? '1' : '0' }
    })
    return r
  },
  async getScheduledSteps({ from, to, onlyMine = false }) {
    const r = await campaignsApi.get('/calendar/scheduled-steps', {
      params: { from, to, onlyMine: onlyMine ? '1' : '0' }
    })
    return r
  },
  async getActive({ onlyMine = false } = {}) {
    const r = await campaignsApi.get('/cohorts/active', {
      params: { onlyMine: onlyMine ? '1' : '0' }
    })
    return r
  },
  async getStepLogs({ from, to, campaignId = null, onlyMine = false, limit = 500 } = {}) {
    const r = await campaignsApi.get('/cohorts/step-logs', {
      params: {
        from,
        to,
        campaign_id: campaignId || undefined,
        onlyMine: onlyMine ? '1' : '0',
        limit,
      }
    })
    return r
  },
  /**
   * Preview bulk enrollment (dry-run sin side-effects).
   * @param {{campaignId: string, startDate: string, contactIds: number[]}} args
   * @returns {Promise<{success: boolean, total: number, ready: number, skipped: object, warning_active_cohort_count: number, warning_active_cohort_breakdown: object[], existing_cohort_id: string|null}>}
   */
  async previewBulk({ campaignId, startDate, contactIds }) {
    const r = await campaignsApi.post('/cohorts/preview-bulk', {
      campaign_id: campaignId,
      start_date: startDate,
      contact_ids: contactIds
    })
    return r
  }
}


// CoSelect: heartbeat y release de presencia
export const presenceAPI = {
  /** Marca al agente como "viendo" un contacto. Si phone es null, libera. */
  heartbeat: (contactPhone) =>
    api.post('/presence/heartbeat', { contact_phone: contactPhone || null }).then(r => r.data),

  /** Libera explicita (uso normal: cambiar de pestana/vista. Para unload usar fetch keepalive desde el hook). */
  release: () =>
    api.post('/presence/release').then(r => r.data),
};


// ========================================
// CAMPAIGN BUILDER API
// ========================================
function buildCampaignBuilderParams(filters) {
  const params = new URLSearchParams();
  if (filters.created_from) params.append('created_from', filters.created_from);
  if (filters.created_to) params.append('created_to', filters.created_to);
  if (filters.include_ic?.length) params.append('include_ic', filters.include_ic.join(','));
  if (filters.exclude_ic?.length) params.append('exclude_ic', filters.exclude_ic.join(','));
  if (filters.include_flag?.length) params.append('include_flag', filters.include_flag.join(','));
  if (filters.exclude_flag?.length) params.append('exclude_flag', filters.exclude_flag.join(','));
  if (filters.exclude_campaign_id?.length) params.append('exclude_campaign_id', filters.exclude_campaign_id.join(','));
  if (filters.agent) params.append('agent', filters.agent);
  if (filters.search) params.append('search', filters.search);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  if (filters.sort) params.append('sort', filters.sort);
  return params.toString();
}

export const campaignBuilderAPI = {
  getContacts: (filters = {}) => {
    const qs = buildCampaignBuilderParams(filters);
    return api.get(`/campaign-builder/contacts?${qs}`);
  },
  getContactIds: (filters = {}) => {
    const qs = buildCampaignBuilderParams(filters);
    return api.get(`/campaign-builder/contacts/ids?${qs}`);
  },
};

export const kbCurationAPI = {
  listProposals: (params) => api.get('/kb-curation/proposals', { params }).then(r => r.data),
  stats: () => api.get('/kb-curation/proposals/stats').then(r => r.data),
  approve: (id, body) => api.post(`/kb-curation/proposals/${id}/approve`, body).then(r => r.data),
  reject: (id) => api.post(`/kb-curation/proposals/${id}/reject`).then(r => r.data),
  rescue: (id) => api.post(`/kb-curation/proposals/${id}/rescue`).then(r => r.data),
  bulkApprove: (body) => api.post('/kb-curation/proposals/bulk-approve', body).then(r => r.data),
  bulkReject: (body) => api.post('/kb-curation/proposals/bulk-reject', body).then(r => r.data),
  rescan: (days) => api.post('/kb-curation/rescan', { days }).then(r => r.data),
  setConfig: (kb_mining_continuo_activo) => api.patch('/kb-curation/config', { kb_mining_continuo_activo }).then(r => r.data),
};

export default api
